import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { ReasonSegment } from '../types';

export interface ReasonSegmentPlayerHandle {
  togglePlayback(): void;
}

export interface ReasonSegmentPlayerProps {
  src: string;
  segment: ReasonSegment;
  segmentIndex: number;
  segmentCount: number;
  onMediaDuration?: (durationS: number) => void;
  onPrevious: () => void;
  onNext: () => void;
}

function seekAndPlay(video: HTMLVideoElement, startTime: number): void {
  video.currentTime = startTime;
  try {
    const result = video.play();
    void result?.catch(() => undefined);
  } catch {
    // Browsers may block programmatic playback until the first user gesture.
  }
}

export const ReasonSegmentPlayer = forwardRef<ReasonSegmentPlayerHandle, ReasonSegmentPlayerProps>(
  function ReasonSegmentPlayer(
    {
      src,
      segment,
      segmentIndex,
      segmentCount,
      onMediaDuration = () => undefined,
      onPrevious,
      onNext,
    },
    ref
  ) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [videoDuration, setVideoDuration] = useState<number | null>(null);
    const [currentTime, setCurrentTime] = useState(segment.start_time_s);
    const [mediaError, setMediaError] = useState(false);

    useImperativeHandle(
      ref,
      () => ({
        togglePlayback() {
          const video = videoRef.current;
          if (!video) return;
          if (video.paused) {
            if (
              video.currentTime < segment.start_time_s ||
              video.currentTime >= segment.end_time_s
            ) {
              video.currentTime = segment.start_time_s;
            }
            try {
              const result = video.play();
              void result?.catch(() => undefined);
            } catch {
              // Browsers may block playback until the first user gesture.
            }
          } else {
            video.pause();
          }
        },
      }),
      [segment.end_time_s, segment.start_time_s]
    );

    useEffect(() => {
      setMediaError(false);
      const video = videoRef.current;
      if (video) {
        setCurrentTime(segment.start_time_s);
        seekAndPlay(video, segment.start_time_s);
      }
    }, [segment.start_time_s]);

    const handleTimeUpdate = () => {
      const video = videoRef.current;
      if (!video) return;
      const effectiveEnd = Math.min(segment.end_time_s, video.duration || segment.end_time_s);
      if (video.currentTime >= effectiveEnd) {
        video.pause();
        video.currentTime = effectiveEnd;
      }
      setCurrentTime(Math.min(Math.max(video.currentTime, segment.start_time_s), effectiveEnd));
    };
    const effectiveEnd =
      videoDuration == null ? segment.end_time_s : Math.min(segment.end_time_s, videoDuration);
    const segmentDuration = Math.max(0, effectiveEnd - segment.start_time_s);
    const relativeTime = Math.min(segmentDuration, Math.max(0, currentTime - segment.start_time_s));
    const sourceDurationMismatch =
      videoDuration != null && videoDuration + 0.001 < segment.end_time_s;

    return (
      <section className="min-w-0" aria-label="Fail segment player">
        <div className="overflow-hidden rounded-lg border border-brand-border bg-black">
          {/* biome-ignore lint/a11y/useMediaCaption: Reason review is visual and the source has no caption track. */}
          <video
            ref={videoRef}
            src={src}
            controls
            preload="metadata"
            onLoadedMetadata={() => {
              setMediaError(false);
              if (videoRef.current) {
                setVideoDuration(videoRef.current.duration);
                onMediaDuration(videoRef.current.duration);
                setCurrentTime(segment.start_time_s);
                seekAndPlay(videoRef.current, segment.start_time_s);
              }
            }}
            onError={() => setMediaError(true)}
            onTimeUpdate={handleTimeUpdate}
            className="aspect-video w-full bg-black object-contain"
            aria-label="SecCap audit preview"
            title="Play or pause (Space)"
          />
        </div>

        {mediaError ? (
          <div
            className="mt-3 border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300"
            role="alert"
          >
            Preview could not be loaded. The submitted Segment and reasons remain available below.
          </div>
        ) : null}

        <div className="mt-3 rounded-md border border-brand-border bg-brand-surface px-3 py-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                const video = videoRef.current;
                if (!video) return;
                setCurrentTime(segment.start_time_s);
                seekAndPlay(video, segment.start_time_s);
              }}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-brand-border text-brand-text-secondary hover:border-brand-orange hover:text-brand-orange"
              aria-label="Replay current Segment"
              title="Replay current Segment"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
            <input
              type="range"
              min={0}
              max={Math.max(segmentDuration, 0.001)}
              step={0.01}
              value={relativeTime}
              onChange={(event) => {
                const video = videoRef.current;
                if (!video) return;
                const nextTime = Math.min(
                  effectiveEnd,
                  segment.start_time_s + Number(event.target.value)
                );
                video.currentTime = nextTime;
                setCurrentTime(nextTime);
              }}
              className="min-w-0 flex-1 accent-brand-orange"
              aria-label={`Segment ${segmentIndex + 1} playback position`}
            />
            <span className="w-28 shrink-0 text-right font-mono text-xs text-brand-text-secondary">
              {relativeTime.toFixed(2)}s / {segmentDuration.toFixed(2)}s
            </span>
          </div>
          {sourceDurationMismatch ? (
            <p className="mt-2 text-xs text-amber-300" role="status">
              Source duration mismatch · playback ends at {effectiveEnd.toFixed(2)}s
            </p>
          ) : null}
        </div>

        <div className="mt-3 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onPrevious}
            disabled={segmentIndex === 0}
            title="Previous Segment (ArrowLeft)"
            className="inline-flex h-9 items-center gap-2 rounded-md border border-brand-border px-3 text-sm text-brand-text-primary disabled:cursor-not-allowed disabled:opacity-35"
          >
            <ChevronLeft className="h-4 w-4" /> Previous
          </button>
          <div className="text-center">
            <div className="font-mono text-sm text-brand-text-primary">
              {segmentIndex + 1} / {segmentCount}
            </div>
            <div className="mt-0.5 text-xs text-brand-text-secondary">
              {segment.start_time_s.toFixed(2)}s – {segment.end_time_s.toFixed(2)}s ·{' '}
              {segmentDuration.toFixed(2)}s
            </div>
          </div>
          <button
            type="button"
            onClick={onNext}
            disabled={segmentIndex >= segmentCount - 1}
            title="Next Segment (ArrowRight)"
            className="inline-flex h-9 items-center gap-2 rounded-md border border-brand-border px-3 text-sm text-brand-text-primary disabled:cursor-not-allowed disabled:opacity-35"
          >
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </section>
    );
  }
);
